import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MonitorSmartphone, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export function TrustedDevices() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [askAll, setAskAll] = useState(false);

  const load = useCallback(() => api.get("/me/devices").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const leave = () => { logout(); toast.info("This device was signed out."); navigate("/login", { replace: true }); };

  const revoke = async (d) => {
    setBusy(d.id);
    try {
      const { data } = await api.delete(`/me/devices/${d.id}`);
      if (data.current) return leave();
      toast.success(`${d.label} signed out.`);
      setRows((r) => r.filter((x) => x.id !== d.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign out that device."); }
    finally { setBusy(null); }
  };

  const revokeAll = async () => {
    setBusy("all");
    try {
      await api.delete("/me/devices");
      if (rows.some((d) => d.current)) return leave();
      toast.success("All remembered devices signed out.");
      setRows([]); setAskAll(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign out devices."); }
    finally { setBusy(null); }
  };

  if (!rows) return null;

  return (
    <section className="rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10" data-testid="account-devices">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sbg-raspberry"><MonitorSmartphone className="h-4 w-4" /> Trusted devices</div>
        {rows.length > 0 && (
          <button data-testid="devices-signout-all-btn" onClick={() => setAskAll(true)} className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-xs font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/20"><LogOut className="h-3.5 w-3.5" /> Sign out everywhere</button>
        )}
      </div>
      <p className="mt-2 text-sm text-sbg-burgundy/70">Devices where you chose "Remember this device" stay signed in for 30 days and skip the inactivity sign-out.</p>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-sbg-burgundy/60" data-testid="devices-empty">No remembered devices. Tick "Remember this device" next time you sign in.</p>
      ) : (
        <ul className="mt-4 divide-y divide-sbg-raspberry/10 rounded-2xl ring-1 ring-sbg-raspberry/10">
          {rows.map((d) => (
            <li key={d.id} data-testid={`device-row-${d.id}`} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-bold text-sbg-burgundy">
                  {d.label}
                  {d.current && <span data-testid="device-current-badge" className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200">This device</span>}
                </div>
                <div className="text-xs text-sbg-burgundy/60">First signed in {fmt(d.created_at)} · Last used {fmt(d.last_seen)}{d.ip ? ` · ${d.ip}` : ""}</div>
              </div>
              <button data-testid={`device-revoke-${d.id}`} onClick={() => revoke(d)} disabled={busy === d.id} className="inline-flex items-center gap-1.5 self-start rounded-full bg-sbg-blush px-3 py-1.5 text-xs font-bold text-sbg-raspberry disabled:opacity-60 sm:self-auto">
                {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />} Sign out
              </button>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog open={askAll} onOpenChange={setAskAll} title="Sign out of all devices?" description="Every remembered device (including this one) will need to sign in again." confirmLabel="Sign out everywhere" onConfirm={revokeAll} />
    </section>
  );
}
